import React, { useEffect, useMemo } from "react";
import { Box, Container, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import { ContractsPanel } from "../components/PatientContractsTab";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { pageHeader } from "../theme/designSystem";
import { ContractSubject } from "../services/contractsService";

/**
 * Contratos do usuário profissional (item 236; RN-558d): página-filha de Usuários, aberta pelo ícone da
 * linha (?page=professional-contracts&professionalId=<UEPR>). Mesmo painel da aba Contratos do
 * paciente, com o contratante trocado: só modelos com destinatário "Profissional" são emitidos aqui.
 */
const ProfessionalContracts: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();

  const params = getAllParams();
  const professionalId = params.get("professionalId") || "";
  const professionalName = params.get("professionalName") || "Profissional";
  const especialidade = params.get("especialidade") || "";
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  useEffect(() => {
    document.title = `${professionalName} / Contratos`;
  }, [professionalName]);

  const subject = useMemo<ContractSubject>(() => ({ kind: "professional", id: professionalId }), [professionalId]);
  const goBack = () => navigateTo("entity-users", { entityId, entityName });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <IconButton className="c4u-page-header__back" onClick={goBack} title="Voltar para Usuários" size="small">
              <ArrowBack />
            </IconButton>
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Contratos do profissional</h1>
              <p className="c4u-page-header__subtitle">Contratos emitidos para o profissional: vigência, assinatura e cancelamento.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            {/* O profissional é o ESCOPO da tela: vem antes da barra, como em Disponibilidade. */}
            <div className="c4u-scope-line">
              <strong>{professionalName}</strong>
              {especialidade ? <span> · {especialidade}</span> : null}
            </div>
            {professionalId ? <ContractsPanel subject={subject} /> : <p>Profissional não informado.</p>}
          </div>
        </Container>
      </Box>

      <FooterInternal />
    </Box>
  );
};

export default ProfessionalContracts;
